import { Divider, Table } from 'antd'
import { SortOrder } from 'antd/lib/table'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors'
import PerText from '../../../components/custom_components/Text'
import { useDateTime } from '../../../shared/useDateTime'
import { TimeZoneAndLocale } from '../../../types.custom'
import useCompleteContractGovernmentJobTitleHistory from '../../../graphql/contractHistory/hooks/useCompleteContractGovernmentJobTitleHistory'
import { ContractGovernmentJobTitle } from '../../../types'

interface Props {
  employeeId: string
  noDivider?: boolean
  resigned?: boolean
  timezone: TimeZoneAndLocale
}

export const GovernmentJobTitleHistoryTable: FC<Props> = ({ employeeId, noDivider = false, resigned, timezone }) => {
  const { formatDateShort } = useDateTime(timezone)
  const { t } = useTranslation()

  // load Data
  const { data, loading, error } = useCompleteContractGovernmentJobTitleHistory(employeeId, resigned)

  if (loading || error || !data) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data} error={error} />
  }

  const contractGovernmentJobTitleHistory = data.contractGovernmentJobTitleHistoryByEmployeeId
  const showHistory = !!contractGovernmentJobTitleHistory && contractGovernmentJobTitleHistory.length > 0

  const columns = [
    {
      key: 'contractGovernmentJobTitle-governmentJobTitle',
      title: t('profile.contract.contractGovernmentJobTitle.governmentJobTitle'),
      className: 'textAlign-left minWidth-column-xl',
      sorter: (aEl, bEl) => {
        const a = aEl.governmentJobTitle.hiringOffice.code.concat(aEl.governmentJobTitle.department).concat(aEl.governmentJobTitle.title)
        const b = bEl.governmentJobTitle.hiringOffice.code.concat(bEl.governmentJobTitle.department).concat(bEl.governmentJobTitle.title)
        return a.localeCompare(b)
      },
      render: (row: ContractGovernmentJobTitle) =>
        !!row.governmentJobTitle &&
        !!row.governmentJobTitle.hiringOffice &&
        `${row.governmentJobTitle.hiringOffice.code} - ${row.governmentJobTitle.department} - ${row.governmentJobTitle.title}`,
    },
    {
      key: 'contractGovernmentJobTitle-fromDate',
      title: t('profile.contract.contractGovernmentJobTitle.fromDate'),
      className: 'textAlign-left minWidth-column-s',
      defaultSortOrder: 'descend' as SortOrder,
      sorter: (dateA, dateB) => {
        const a = dateA.fromDate ? dateA.fromDate : ' '
        const b = dateB.fromDate ? dateB.fromDate : ' '
        return a.localeCompare(b)
      },
      render: (row: ContractGovernmentJobTitle) => row.fromDate && formatDateShort(row.fromDate),
    },
    {
      key: 'contractGovernmentJobTitle-toDate',
      title: t('profile.contract.contractGovernmentJobTitle.toDate'),
      className: 'textAlign-left minWidth-column-s',
      sorter: (dateA, dateB) => {
        const a = dateA.toDate ? dateA.toDate : ' '
        const b = dateB.toDate ? dateB.toDate : ' '
        return a.localeCompare(b)
      },
      render: (row: ContractGovernmentJobTitle) => row.toDate && formatDateShort(row.toDate),
    },
    {
      key: 'contractGovernmentJobTitle-note',
      className: 'minWidth-column-xl',
      title: t('profile.contract.contractGovernmentJobTitle.note'),
      render: (row: ContractGovernmentJobTitle) => row.note && <PerText>{row.note}</PerText>,
    },
  ]

  return (
    <>
      {showHistory && (
        <>
          {!noDivider && <Divider />}
          <PerText appearance="tile-heading" mb="s">
            {t('profile.contract.contractGovernmentJobTitle.header')}
          </PerText>
          <Table
            dataSource={contractGovernmentJobTitleHistory.map((contractGovernmentJobTitle) => ({
              ...contractGovernmentJobTitle,
              key: contractGovernmentJobTitle.id,
            }))}
            className="small"
            columns={columns}
            pagination={false}
          />
        </>
      )}
    </>
  )
}
